import { Object3D, Raycaster, Vector3 } from 'three';

import { isDefined } from '../../../utils/variables';
import { SkyviewCamera } from '../../cameras/types';
import { CoordinateManager } from '../../coordinate-manager';
import { Conic } from '../../elements/design/design-node/utils/conic';
import { findPointOnLineFromHeight } from '../../utilities/math-helpers';
import { Pick } from '../../utilities/pick';
import { isAssetPickable } from '../select-tool';
import { chooseSnapCandidate, findAssetSnapCandidates } from './snap-candidates';
import { SnapStyle, SnapStyleOption } from './snap-style';

export type AssetSnapSettings = {
  /** The snap styles that are on, several at the same time, see chooseSnapCandidate. */
  styles: SnapStyleOption[];
  pointCloud: boolean;
  tiledModel: boolean;
  /** Snaps towards the assets, except the excluded ones. */
  assets: boolean;
  /** Sets the height of the snapped point, or offsets it, like in the design tool. */
  height?: 'absolute' | 'offset';
  absoluteHeight: number;
  heightOffset: number;
};

/**
 * The settings and the excluded assets of AssetSnapping, for useSyncExternalStore.
 */
type AssetSnappingSnapshot = { settings: AssetSnapSettings; excludedSnapAssetIds: Set<string> };

/**
 * Where the cursor snaps to, and the curves whose center it is, to highlight.
 */
export type AssetSnapPoint = { point?: Vector3; curves?: Conic[] };

/** How far from the cursor (pixels) the points of the snap styles are snapped to. */
const pixelSnapDistance = 20;

/**
 * The snapping of a tool towards the assets, the point cloud, the tiled model and a height, with
 * the snap styles, set in the snapping dialog: for the measure tool, the POI tool and moving the
 * points of a line or an area.
 */
export class AssetSnapping {
  settings: AssetSnapSettings;
  /**
   * Assets that are not snapped towards, by uuid.
   */
  excludedSnapAssetIds = new Set<string>();
  private snapshot: AssetSnappingSnapshot;
  private readonly listeners = new Set<() => void>();

  constructor(settings: Partial<AssetSnapSettings>) {
    this.settings = {
      styles: ['free'],
      pointCloud: true,
      tiledModel: true,
      assets: false,
      absoluteHeight: 0,
      heightOffset: 0,
      ...settings,
    };
    this.snapshot = { settings: this.settings, excludedSnapAssetIds: this.excludedSnapAssetIds };
  }

  onSettingsChanged(newValue: Partial<AssetSnapSettings>) {
    this.settings = { ...this.settings, ...newValue };
    this.notify();
  }

  onExcludedSnapAssetsChanged(assetIds: Set<string>) {
    this.excludedSnapAssetIds = new Set(assetIds);
    this.notify();
  }

  /**
   * Calls the listener whenever the snapping changes, for useSyncExternalStore. Returns a function
   * that unsubscribes.
   */
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The settings and the excluded assets, a new object whenever they change.
   */
  getSnapshot = () => this.snapshot;

  private notify() {
    this.snapshot = { settings: this.settings, excludedSnapAssetIds: this.excludedSnapAssetIds };
    this.listeners.forEach((listener) => listener());
  }

  /**
   * Whether an asset is snapped towards.
   */
  isSnapAsset = (asset: Object3D) =>
    isAssetPickable(asset) && !this.excludedSnapAssetIds.has(asset.uuid);

  /**
   * Finds where the cursor snaps to, from the ray through it. There is no point where nothing is
   * snapped to, e.g. away from the points of the snap styles without free snapping.
   * @param options.predicate The assets that can be snapped to, of those that are snapped towards.
   * @param options.fallbackHeight The height that free snapping falls back to, where nothing is
   * hovered.
   */
  findSnapPoint(
    camera: SkyviewCamera,
    rayCaster: Raycaster,
    options?: { predicate?: (asset: Object3D) => boolean; fallbackHeight?: number },
  ): AssetSnapPoint {
    const { styles, pointCloud, tiledModel, assets } = this.settings;
    const snapStyles = styles.filter((x): x is SnapStyle => x !== 'free');
    const free = styles.includes('free');
    const predicate = (asset: Object3D) =>
      this.isSnapAsset(asset) && (options?.predicate?.(asset) ?? true);
    const { origin, direction } = rayCaster.ray;

    // The snap styles target lines, so the outline of a filled shape is preferred over its fill.
    const assetIntersection = assets
      ? Pick.instance.closestAssetIntersection(
          camera,
          rayCaster,
          predicate,
          snapStyles.length > 0 ? pixelSnapDistance : undefined,
        )
      : undefined;
    const styled = assets
      ? chooseSnapCandidate(
          findAssetSnapCandidates(
            camera,
            rayCaster,
            snapStyles,
            assetIntersection,
            predicate,
            pixelSnapDistance,
          ),
          free,
          pixelSnapDistance,
        )
      : undefined;

    // The hovered position on the asset, point cloud or tiled model.
    const freePoint = !free
      ? undefined
      : assetIntersection
        ? Pick.instance.getIntersectionPoint(assetIntersection)
        : pointCloud || tiledModel
          ? Pick.instance.closestPoint(camera, rayCaster, {
              skipAssets: true,
              skipPointCloud: !pointCloud,
              skipTiledModel: !tiledModel,
            })
          : undefined;
    let point = styled?.point ?? freePoint;

    switch (this.settings.height) {
      case 'absolute': {
        const height = this.settings.absoluteHeight - CoordinateManager.getInstance().getMAS();
        point =
          point?.clone().setZ(height) ??
          (free ? findPointOnLineFromHeight(origin, direction, height) : undefined);
        break;
      }
      case 'offset':
        point = point?.clone().setZ(point.z + this.settings.heightOffset);
        break;
    }

    if (!point && free && isDefined(options?.fallbackHeight)) {
      point = findPointOnLineFromHeight(origin, direction, options.fallbackHeight);
    }

    return { point, curves: point && styled?.curves };
  }
}
